/**
 * ReportEditor — small textarea-based editor for the AI compliance report.
 *
 * - Shows rendered markdown by default
 * - "Edit" turns it into a monospaced textarea
 * - "Save" PUTs /inspections/:id/report (logs an audit event server-side)
 * - "Finalise" locks the report as the customer-ready version
 *
 * Intentionally minimal markdown (same MarkdownLite the page already uses) so
 * we don't pull in a heavy WYSIWYG dependency on a PWA used at poolside.
 */
import { useEffect, useState } from "react";
import { Pencil, Save, X, ShieldCheck, RotateCcw, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { snapshotInspection, cacheReportPdfs } from "@/lib/offlineReports";

const ReportEditor = ({
  inspectionId,
  reportText,
  finalized,
  finalizedAt,
  hasOriginal,
  onUpdated,
  renderReadOnly,   // function(text) -> JSX (renders the markdown)
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reportText || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(reportText || ""); }, [reportText]);

  const startEdit = () => {
    if (finalized) {
      toast.error("Unfinalise the report first to edit it");
      return;
    }
    setEditing(true);
  };

  const cancel = () => { setDraft(reportText || ""); setEditing(false); };

  const save = async () => {
    if (!draft.trim()) { toast.error("Report cannot be empty"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/inspections/${inspectionId}/report`, { ai_report: draft });
      onUpdated(r.data);
      setEditing(false);
      toast.success("Report saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    if (!window.confirm("Finalise this report? You can still unfinalise later, but every change is recorded in the audit log.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/inspections/${inspectionId}/report/finalize`);
      onUpdated(r.data);
      // Snapshot + cache the PDF BEFORE telling the user we're done, so the success
      // message is honest about the offline copy being available.
      snapshotInspection(r.data);
      let cached = { pdf: false };
      try { cached = await cacheReportPdfs(inspectionId); } catch { /* best-effort */ }
      toast.success(cached.pdf ? "Report finalised & cached for offline viewing" : "Report finalised");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Finalise failed");
    } finally { setBusy(false); }
  };

  const unfinalize = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/inspections/${inspectionId}/report/unfinalize`);
      onUpdated(r.data);
      toast.success("Report unfinalised — you can edit again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unfinalise failed");
    } finally { setBusy(false); }
  };

  if (!reportText) return null;

  return (
    <div data-testid="report-editor">
      {finalized && (
        <div data-testid="report-finalized-banner" className="mb-3 px-3 py-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider bg-emerald-50 border-2 border-emerald-400 text-emerald-900">
          <Lock size={12} strokeWidth={3}/> Finalised
          {finalizedAt && (
            <span className="font-normal normal-case ml-1 text-emerald-800">
              · {new Date(finalizedAt).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {!editing ? (
        <>
          <div data-testid="ai-report-content">{renderReadOnly(reportText)}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={startEdit} disabled={busy || finalized} data-testid="edit-report-button"
              className="h-10 px-4 inline-flex items-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-wider hover:bg-slate-100 disabled:opacity-40">
              <Pencil size={14} strokeWidth={2.5}/> Edit summary
            </button>
            {!finalized && (
              <button onClick={finalize} disabled={busy} data-testid="finalize-report-button"
                className="h-10 px-4 inline-flex items-center gap-2 bg-emerald-600 border-2 border-emerald-600 text-white font-bold text-xs uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <ShieldCheck size={14} strokeWidth={2.5}/>} Finalise
              </button>
            )}
            {finalized && (
              <button onClick={unfinalize} disabled={busy} data-testid="unfinalize-report-button"
                className="h-10 px-4 inline-flex items-center gap-2 bg-white border-2 border-amber-500 text-amber-800 font-bold text-xs uppercase tracking-wider hover:bg-amber-50 disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <RotateCcw size={14} strokeWidth={2.5}/>} Unfinalise
              </button>
            )}
          </div>
        </>
      ) : (
        <div data-testid="report-editor-active">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
            Edit summary — supports basic markdown (## heading, **bold**, - bullets)
          </p>
          <textarea
            data-testid="report-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={20}
            className="w-full p-3 text-sm font-mono border-2 border-slate-300 focus:border-[#1E1B4B] focus:outline-none bg-white"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={save} disabled={busy} data-testid="save-report-button"
              className="h-10 px-4 inline-flex items-center gap-2 bg-[#1E1B4B] border-2 border-[#1E1B4B] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#0F0E2E] disabled:opacity-40">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Save size={14} strokeWidth={2.5}/>} Save changes
            </button>
            <button onClick={cancel} disabled={busy} data-testid="cancel-report-edit-button"
              className="h-10 px-4 inline-flex items-center gap-2 bg-white border-2 border-slate-300 text-slate-700 font-bold text-xs uppercase tracking-wider hover:border-slate-900 disabled:opacity-40">
              <X size={14} strokeWidth={2.5}/> Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportEditor;
